/**
 * InviteCodeStep
 *
 * Story 10: Provider Invite Code Validation
 *
 * Step 1 of 5 in registration wizard
 *
 * Features:
 * - Invite code input field
 * - Client-side format validation
 * - Server-side validation on blur
 * - Display validation result
 * - Show invite details (county, tier, expiration) on valid
 * - Prevent next step on invalid
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { InviteCodeInput } from '../InviteCodeInput';
import { useInviteValidation } from '../../hooks/useInviteValidation';
import type { InviteValidationResult } from '../../types';

const inviteCodeSchema = z.object({
  code: z.string().min(8, 'Code must be at least 8 characters').max(12, 'Code must be at most 12 characters').regex(/^[A-Z0-9]+$/, 'Code must contain only uppercase letters and numbers'),
});

type InviteCodeFormData = z.infer<typeof inviteCodeSchema>;

interface InviteCodeStepProps {
  onNext: (code: string) => void;
  initialCode?: string;
}

const ERROR_MESSAGES = {
  INVALID_CODE: "This invite code doesn't exist",
  EXPIRED: 'This invite code has expired',
  ALREADY_USED: 'This invite code has already been used',
  REVOKED: 'This invite code has been revoked',
};

export function InviteCodeStep({ onNext, initialCode = '' }: InviteCodeStepProps) {
  const [validationResult, setValidationResult] = useState<InviteValidationResult | null>(null);
  const { mutate: validateInvite, isPending: isValidating } = useInviteValidation();

  const {
    watch,
    setValue,
    formState: { errors },
  } = useForm<InviteCodeFormData>({
    resolver: zodResolver(inviteCodeSchema),
    defaultValues: { code: initialCode },
  });

  const code = watch('code');

  const handleValidate = () => {
    if (errors.code || !code) return;

    validateInvite(code, {
      onSuccess: (result) => {
        setValidationResult(result);
      },
    });
  };

  const handleNext = () => {
    if (validationResult?.valid) {
      onNext(code);
    }
  };

  const isValid = validationResult?.valid === true;
  const errorMessage = validationResult?.error ? ERROR_MESSAGES[validationResult.error] : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Enter Your Invite Code</h2>
        <p className="text-neutral-600">
          You should have received an invite code from your county administrator.
        </p>
      </div>

      <div className="space-y-4">
        <InviteCodeInput
          value={code}
          onChange={(value) => {
            setValue('code', value);
            setValidationResult(null);
          }}
          onBlur={handleValidate}
          error={errors.code?.message || errorMessage}
          disabled={isValidating}
        />

        {!isValidating && !validationResult && code.length >= 8 && !errors.code && (
          <button
            type="button"
            onClick={handleValidate}
            className="btn-secondary"
          >
            Validate Code
          </button>
        )}

        {isValidating && (
          <div className="flex items-center text-sm text-neutral-600">
            <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full mr-2" />
            Validating...
          </div>
        )}

        {isValid && validationResult.invite && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Valid Invite Code</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p><strong>County:</strong> {validationResult.invite.county}</p>
                  <p><strong>Tier:</strong> {validationResult.invite.tier}</p>
                  <p>
                    <strong>Expires:</strong>{' '}
                    {new Date(validationResult.invite.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleNext}
          disabled={!isValid || isValidating}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}
